import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { Container } from './Container';
import { salesProof, type Review } from '../data/salesProof';
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function initials(name: string) {
  return name.replace(/\./g, '').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}
function formatDate(date: string) {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}
function Stars({
  value
}: {
  value: number;
}) {
  const filled = Math.max(0, Math.min(5, Math.round(value)));
  return <span className="flex gap-0.5" role="img" aria-label={`${filled} de 5 estrellas`} data-id="element-352" data-mp="3f15p4qdiuhgf">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} aria-hidden="true" className={`h-4 w-4 sm:h-[18px] sm:w-[18px] ${i < filled ? 'fill-sun text-sun' : 'fill-navy/10 text-navy/10'}`} data-id="element-353" data-mp="3jzp9imcgvl6o" data-mpi={i} />)}
    </span>;
}
function ReviewItem({
  r
}: {
  r: Review;
}) {
  return <article className="flex h-full flex-col" data-id="element-354" data-mp="2hf99zi7ye3w3">
      <div className="flex items-center gap-4" data-id="element-355" data-mp="2dcw66zcrog1y">
        <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-teal font-heading text-lg font-extrabold text-white md:h-[68px] md:w-[68px] md:text-xl" data-id="element-356" data-mp="2de7kht3u967b">
          {initials(r.name)}
        </span>
        <div className="min-w-0" data-id="element-357" data-mp="30ctxolyzewf4">
          <Stars value={r.rating} data-id="element-358" data-mp="30bsnejpsidyg" />
          <p className="mt-1.5 truncate text-base font-bold text-navy md:text-lg" data-id="element-359" data-mp="2wbqky8c8dgi4">{r.name}</p>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-navy/60" data-id="element-360" data-mp="2w9xmvhfoo4lq">
            {r.verifiedPurchase && <>
                <span className="inline-flex items-center gap-1.5" data-id="element-361" data-mp="3o1vcqs2bl5td">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal text-white" data-id="element-362" data-mp="1m5nxcrf5cwms">
                    <CheckIcon aria-hidden="true" className="h-2.5 w-2.5" strokeWidth={3.5} data-id="element-363" data-mp="1r50gp26t7lkm" />
                  </span>
                  Compra verificada
                </span>
                <span aria-hidden="true" data-id="element-364" data-mp="1henyhgmgw4hn">·</span>
              </>}
            <time dateTime={r.date} data-id="element-365" data-mp="1hcl0a9b5v51u">{formatDate(r.date)}</time>
          </p>
        </div>
      </div>
      <blockquote className="mt-6" data-id="element-366" data-mp="13gzgcgjsbbga">
        {r.title && <p className="mb-2 font-heading text-lg font-extrabold text-navy" data-id="element-367" data-mp="18e9b8bpy0fbi">{r.title}</p>}
        <p className="text-[17px] font-medium italic leading-[1.5] text-[#26364D] sm:text-lg md:text-[20px]" data-id="element-368" data-mp="th8iq5zxggbz">“{r.text}”</p>
      </blockquote>
    </article>;
}
function ReviewSlot({
  n
}: {
  n: number;
}) {
  return <div role="img" aria-label={`Espacio reservado: reseña real ${n}`} className="flex flex-col" data-id="element-369" data-mp="2nvt0a9sybvr5">
      <div className="flex items-center gap-4" data-id="element-370" data-mp="2ssrs2lp57qm6">
        <span className="h-14 w-14 shrink-0 rounded-full border-2 border-dashed border-teal/40 md:h-[68px] md:w-[68px]" data-id="element-371" data-mp="2srgpgp7foown" />
        <div className="grid gap-2" data-id="element-372" data-mp="c7o6yundygi5">
          <span className="h-3 w-24 rounded-full bg-sun/40" data-id="element-373" data-mp="c8pg2tkucsx8" />
          <span className="h-3.5 w-28 rounded-full bg-navy/10" data-id="element-374" data-mp="qw0tz24hwp0i" />
          <span className="h-3 w-40 rounded-full bg-navy/[0.06]" data-id="element-375" data-mp="qydijy4rdxmj" />
        </div>
      </div>
      <div className="mt-6 grid gap-2.5" data-id="element-376" data-mp="m49cri78bvn1">
        <span className="font-mono text-xs font-semibold uppercase tracking-wide text-teal-dark" data-id="element-377" data-mp="zwp9k19jexfi">[Reseña real #{n}]</span>
        <span className="h-3.5 w-full rounded-full bg-navy/[0.06]" data-id="element-378" data-mp="vuc1v7b8cpg3" />
        <span className="h-3.5 w-11/12 rounded-full bg-navy/[0.06]" data-id="element-379" data-mp="19uwlg5oyypan" />
        <span className="h-3.5 w-2/3 rounded-full bg-navy/[0.06]" data-id="element-380" data-mp="19t2a9j3i2bjr" />
      </div>
    </div>;
}
function ArrowButton({
  dir,
  disabled,
  onClick
}: {
  dir: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = dir === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={dir === 'prev' ? 'Ver reseñas anteriores' : 'Ver más reseñas'} className="flex h-11 w-11 items-center justify-center rounded-full border border-teal/30 bg-white text-teal-dark transition-[background-color,opacity] duration-150 ease-out hover:bg-teal-soft disabled:cursor-default disabled:opacity-35 disabled:hover:bg-white" data-id="element-381" data-mp="36p9g07n2189s">
      <Icon aria-hidden="true" className="h-5 w-5" data-id="element-382" data-mp="2peclswchyqs8" />
    </button>;
}
export function Reviews() {
  // Orden cronológico: de la más antigua a la más reciente.
  const reviews = [...salesProof.reviews].sort((a, b) => a.date.localeCompare(b.date));
  const count = reviews.length;
  const trackRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, count]);
  const scrollByItem = (dir: 1 | -1) => {
    const el = trackRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    el.scrollBy({
      left: dir * first.offsetWidth,
      behavior: 'smooth'
    });
  };
  const items = count === 0 ? null : reviews;
  return <section aria-labelledby="reviews-title" className="overflow-hidden bg-cream py-16 sm:py-20 lg:py-24" data-id="element-383" data-mp="50xwe7eo6cq9">
      <Container data-id="element-384" data-mp="1170xqe778fe">
        <div className="mx-auto max-w-[1160px]" data-id="element-385" data-mp="10nepxhilxvk">
          <header className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-386" data-mp="3s78dk3dtaa35">
            <div className="max-w-2xl" data-id="element-387" data-mp="3n858wk1qu5vp">
              <p className="font-display text-xs font-extrabold uppercase tracking-[0.14em] text-teal-dark sm:text-sm" data-id="element-388" data-mp="3n73xufkq9jdz">
                Experiencias de quienes ya lo usan
              </p>
              <h2 id="reviews-title" className="mt-3 text-balance font-heading text-[1.85rem] font-black leading-[1.1] text-navy sm:text-4xl lg:text-[2.6rem]" data-id="element-389" data-mp="38k58qr9cf12f">
                Lo que dicen sobre Viandas Express 15’
              </h2>
              <p className="mt-4 text-base leading-relaxed text-navy/70 sm:text-lg" data-id="element-390" data-mp="33l22x4ljgqte">
                Experiencias compartidas por compradores del producto.
              </p>
            </div>
            <div className="flex shrink-0 gap-2" data-id="element-391" data-mp="5mwsega71dj">
              <ArrowButton dir="prev" disabled={atStart} onClick={() => scrollByItem(-1)} data-id="element-392" data-mp="77olwb7o67p" />
              <ArrowButton dir="next" disabled={atEnd} onClick={() => scrollByItem(1)} data-id="element-393" data-mp="3iiuexmrvmx2u" />
            </div>
          </header>

          <ul ref={trackRef} onScroll={updateEdges} aria-label="Reseñas de compradores" className="scrollbar-hide -mx-5 mt-12 flex snap-x snap-mandatory overflow-x-auto scroll-smooth border-y border-navy/[0.08] px-5 sm:mx-0 sm:mt-16 sm:px-0" data-id="element-394" data-mp="3miboosec6gdt">
            {items ? items.map((r, i) => <li key={r.name + r.date} aria-label={`Reseña ${i + 1} de ${count}`} className="w-[86%] shrink-0 snap-start border-l border-navy/[0.08] px-6 py-9 first:border-l-0 first:pl-0 sm:w-[46%] md:px-10 md:py-11 lg:w-[38%]" data-id="element-395" data-mp="1ymdrqcehx848" data-mpi={i}>
                    <ReviewItem r={r} data-id="element-396" data-mp="1kp8f1tka1b00" />
                  </li>) : [1, 2, 3].map(n => <li key={n} className="w-[86%] shrink-0 snap-start border-l border-navy/[0.08] px-6 py-9 first:border-l-0 first:pl-0 sm:w-[46%] md:px-10 md:py-11 lg:w-[38%]" data-id="element-397" data-mp="1ulbrptcr8s17">
                    <ReviewSlot n={n} data-id="element-398" data-mp="35zfkwxbiqc57" />
                  </li>)}
          </ul>

          {count > 0 && <p className="mt-5 text-sm text-navy/60" data-id="element-399" data-mp="310vxesha0saq">
              {count} {count === 1 ? 'reseña' : 'reseñas'} · deslizá para ver todas
            </p>}
        </div>
      </Container>
    </section>;
}